import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { CATEGORIES } from "../lib/constants";
import { MapPin, Plus, X, Route } from "lucide-react";

export default function PostErrand() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pods, setPods] = useState([]);
  const [stops, setStops] = useState([]);
  const multi = stops.length > 0;
  const setMode = (mode) => {
    if (mode === "multi") setStops((s) => (s.length ? s : [{ label: "", details: "", location: "", exact_location: "" }]));
    else setStops([]);
  };
  const [form, setForm] = useState({ title: "", description: "", categories: ["groceries"], location: "", exact_location: "", budget: "", deadline: "", recurrence: "none", recurrence_start: "", recurrence_end: "", pod_id: "" });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleCategory = (v) => setForm((f) => ({
    ...f,
    categories: f.categories.includes(v) ? f.categories.filter((c) => c !== v) : [...f.categories, v],
  }));
  const addStop = () => setStops((s) => [...s, { label: "", details: "", location: "", exact_location: "" }]);
  const updStop = (i, k) => (e) => setStops((s) => s.map((st, idx) => (idx === i ? { ...st, [k]: e.target.value } : st)));
  const removeStop = (i) => setStops((s) => s.filter((_, idx) => idx !== i));

  useEffect(() => { api.get("/pods/mine").then((r) => setPods(r.data)).catch((e) => console.error("Failed to load pods", e)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.budget || Number(form.budget) <= 0) return toast.error("Enter a valid budget");
    if (form.categories.length === 0) return toast.error("Pick at least one category");
    if (form.recurrence !== "none" && form.recurrence_start && form.recurrence_end && form.recurrence_end < form.recurrence_start) {
      return toast.error("Finish date must be on or after the start date");
    }
    const cleanStops = stops.filter((s) => s.label.trim());
    if (stops.length && !cleanStops.length) return toast.error("Give each stop a task name, or remove empty stops");
    setLoading(true);
    try {
      const payload = { ...form, budget: Number(form.budget), pod_id: form.pod_id || null, stops: cleanStops.length ? cleanStops : null };
      const { data } = await api.post("/errands", payload);
      toast.success("Errand posted!");
      navigate(`/errand/${data.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 py-8">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#1A1D20] mb-1">Request an Errand</h1>
        <p className="text-[#6B7280] mb-6">Request an errand — a verified local neighbor will lend a hand.</p>

        <form onSubmit={submit} className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6 sm:p-8 space-y-5">
          {/* Choose errand type first */}
          <div data-testid="post-stops-section">
            <Label className="font-semibold text-sm mb-1.5 block flex items-center gap-1.5"><Route size={15} className="text-primary" /> What kind of errand is this?</Label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="mode-single" onClick={() => setMode("single")}
                className={`rounded-2xl border-2 px-4 py-3 text-left transition-colors ${!multi ? "border-primary bg-primary/5" : "border-gray-200"}`}>
                <span className="flex items-center gap-1.5 font-bold text-sm text-[#1A1D20]"><MapPin size={15} /> Single location</span>
                <span className="text-[11px] text-[#6B7280]">One place, one task</span>
              </button>
              <button type="button" data-testid="mode-multi" onClick={() => setMode("multi")}
                className={`rounded-2xl border-2 px-4 py-3 text-left transition-colors ${multi ? "border-primary bg-primary/5" : "border-gray-200"}`}>
                <span className="flex items-center gap-1.5 font-bold text-sm text-[#1A1D20]"><Route size={15} /> Multiple stops</span>
                <span className="text-[11px] text-[#6B7280]">A run across several places</span>
              </button>
            </div>

            {multi && (
              <div className="mt-4 space-y-3">
                <p className="text-[11px] text-[#6B7280]">Add each stop below. The runner gets a map itinerary and checks each one off as they go.</p>
                {stops.map((s, i) => (
                  <div key={i} data-testid={`stop-row-${i}`} className="rounded-2xl border border-gray-200 p-4 bg-[#FDFBF7]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#4A5361]"><span className="w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-[11px]">{i + 1}</span> Stop {i + 1}</span>
                      <button type="button" data-testid={`stop-remove-${i}`} onClick={() => removeStop(i)} className="text-[#B23B3B] hover:bg-[#FBE9E9] rounded-full p-1"><X size={16} /></button>
                    </div>
                    <Input data-testid={`stop-label-${i}`} value={s.label} onChange={updStop(i, "label")} placeholder="Task (e.g. Pick up dry cleaning)"
                      className="rounded-xl h-11 border-gray-200 mb-2" />
                    <Input data-testid={`stop-details-${i}`} value={s.details} onChange={updStop(i, "details")} placeholder="Details (optional — e.g. ticket #A19, ask for Sam)"
                      className="rounded-xl h-11 border-gray-200 mb-2" />
                    <div className="grid sm:grid-cols-2 gap-2">
                      <Input data-testid={`stop-location-${i}`} value={s.location} onChange={updStop(i, "location")} placeholder="General area (public)"
                        className="rounded-xl h-11 border-gray-200" />
                      <Input data-testid={`stop-exact-${i}`} value={s.exact_location} onChange={updStop(i, "exact_location")} placeholder="Exact address (private)"
                        className="rounded-xl h-11 border-gray-200" />
                    </div>
                  </div>
                ))}
                <button type="button" data-testid="add-stop-btn" onClick={addStop}
                  className="w-full inline-flex items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-primary/60 text-primary font-bold text-sm py-2.5 hover:bg-primary/5">
                  <Plus size={16} /> Add another stop
                </button>
              </div>
            )}
          </div>

          <div>
            <Label className="font-semibold text-sm">Title</Label>
            <Input data-testid="post-title-input" value={form.title} onChange={upd("title")} required placeholder="Pick up groceries from Trader Joe's"
              className="mt-1.5 rounded-2xl h-12 border-gray-200" />
          </div>
          <div>
            <Label className="font-semibold text-sm">Description</Label>
            <Textarea data-testid="post-description-input" value={form.description} onChange={upd("description")} required rows={4}
              placeholder="Share the details — items, quantities, drop-off spot, anything helpful."
              className="mt-1.5 rounded-2xl border-gray-200" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-semibold text-sm mb-1.5 block">Category <span className="text-[#9CA3AF] font-normal">(pick one or more)</span></Label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button type="button" key={c.value} data-testid={`post-cat-${c.value}`} onClick={() => toggleCategory(c.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${form.categories.includes(c.value) ? "border-primary bg-primary/5 text-primary" : "border-gray-200 text-[#6B7280]"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Label className="font-semibold text-sm">General area (public)</Label>
              <Input data-testid="post-location-input" value={form.location} onChange={upd("location")} required placeholder="Brooklyn, NY"
                className="mt-1.5 rounded-2xl h-12 border-gray-200" />
            </div>
            <div>
              <Label className="font-semibold text-sm">Budget ($)</Label>
              <Input data-testid="post-budget-input" type="number" min="1" step="1" value={form.budget} onChange={upd("budget")} required placeholder="20"
                className="mt-1.5 rounded-2xl h-12 border-gray-200" />
            </div>
          </div>
          <div>
            <Label className="font-semibold text-sm">Exact address (private)</Label>
            <Input data-testid="post-exact-location-input" value={form.exact_location} onChange={upd("exact_location")} placeholder="Dorm B, Room 204 — 123 Main St, Apt 4B"
              className="mt-1.5 rounded-2xl h-12 border-gray-200" />
            <p className="text-[11px] text-[#6B7280] mt-1.5">Kept private. Only the runner who accepts — and their emergency contacts — can see this.</p>
          </div>
          <div>
            <Label className="font-semibold text-sm">Deadline</Label>
            <Input data-testid="post-deadline-input" value={form.deadline} onChange={upd("deadline")} required placeholder="Today by 6pm"
              className="mt-1.5 rounded-2xl h-12 border-gray-200" />
          </div>
          <div>
            <Label className="font-semibold text-sm mb-1.5 block">Repeat</Label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { v: "none", l: "One-time" },
                { v: "daily", l: "Daily" },
                { v: "weekly", l: "Weekly" },
                { v: "monthly", l: "Monthly" },
              ].map((o) => (
                <button type="button" key={o.v} data-testid={`post-recurrence-${o.v}`} onClick={() => setForm({ ...form, recurrence: o.v })}
                  className={`rounded-2xl border-2 py-2.5 text-sm font-bold transition-colors ${form.recurrence === o.v ? "border-primary bg-primary/5 text-primary" : "border-gray-200 text-[#6B7280]"}`}>
                  {o.l}
                </button>
              ))}
            </div>
            {form.recurrence !== "none" && (
              <div className="mt-3 space-y-3">
                <p className="text-[11px] text-[#6B7280]">A fresh copy is auto-posted each time this errand is completed, within the date span below.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-semibold text-sm">Start date</Label>
                    <Input data-testid="post-recurrence-start-input" type="date" value={form.recurrence_start} onChange={upd("recurrence_start")}
                      className="mt-1.5 rounded-2xl h-12 border-gray-200" />
                  </div>
                  <div>
                    <Label className="font-semibold text-sm">Finish date</Label>
                    <Input data-testid="post-recurrence-end-input" type="date" value={form.recurrence_end} min={form.recurrence_start || undefined} onChange={upd("recurrence_end")}
                      className="mt-1.5 rounded-2xl h-12 border-gray-200" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {pods.length > 0 && (
            <div>
              <Label className="font-semibold text-sm mb-1.5 block">Post to</Label>
              <div className="flex flex-wrap gap-2">
                <button type="button" data-testid="pod-target-everyone" onClick={() => setForm({ ...form, pod_id: "" })}
                  className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors ${!form.pod_id ? "border-primary bg-primary/5 text-primary" : "border-gray-200 text-[#6B7280]"}`}>
                  🌍 Everyone
                </button>
                {pods.map((p) => (
                  <button type="button" key={p.id} data-testid={`pod-target-${p.id}`} onClick={() => setForm({ ...form, pod_id: p.id })}
                    className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors ${form.pod_id === p.id ? "border-primary bg-primary/5 text-primary" : "border-gray-200 text-[#6B7280]"}`}>
                    🔒 {p.name}
                  </button>
                ))}
              </div>
              {form.pod_id && <p className="text-[11px] text-[#6B7280] mt-1.5">Only members of this pod will see and can accept this errand.</p>}
            </div>
          )}
          <Button data-testid="post-submit-btn" type="submit" disabled={loading}
            className="w-full rounded-full h-12 font-bold bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">
            {loading ? "Requesting…" : "Request an Errand"}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
